
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { changelogApi, type ChangelogState, type FeedRelease } from '@/shared/api';
import { queryClient } from '@/shared/lib/query-client';

export const changelogKeys = {
  all: ['changelog'] as const,
  feed: () => [...changelogKeys.all, 'feed'] as const,
  state: () => [...changelogKeys.all, 'state'] as const,
};

export const feedQuery = queryOptions({
  queryKey: changelogKeys.feed(),
  queryFn: async (): Promise<FeedRelease[]> => {
    const res = await changelogApi.feed();
    const data = (res as unknown as { data?: FeedRelease[] }).data;
    return Array.isArray(data) ? data : [];
  },
});

export const stateQuery = queryOptions({
  queryKey: changelogKeys.state(),
  queryFn: async (): Promise<ChangelogState> => {
    const res = await changelogApi.state();
    const data = (res as unknown as { data?: ChangelogState }).data;
    return data ?? { hasUnread: false, importantRelease: null, version: null };
  },
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: true,
});

export const useChangelogFeed = () => useQuery(feedQuery);

export const useChangelogState = (enabled: boolean) =>
  useQuery({ ...stateQuery, enabled });

export const useMarkChangelogSeen = () =>
  useMutation({
    mutationFn: async () => {
      await changelogApi.markSeen();
    },
    onSuccess: () => {
      queryClient.setQueryData(changelogKeys.state(), (prev: ChangelogState | undefined) =>
        prev ? { ...prev, hasUnread: false } : prev,
      );
    },
  });

export const useMarkImportantSeen = () =>
  useMutation({
    mutationFn: async () => {
      await changelogApi.markImportantSeen();
    },
    onSuccess: () => {
      queryClient.setQueryData(changelogKeys.state(), (prev: ChangelogState | undefined) =>
        prev ? { ...prev, importantRelease: null } : prev,
      );
    },
  });
